import { UserRole } from '../types/user.types'

export const PERMISSIONS: Record<string, UserRole[]> = {

  // Document permissions
  ACCESS_DOCUMENTS: [UserRole.ADMIN, UserRole.EDUCATOR, UserRole.DIRECTIVE, UserRole.SUPERADMIN],
  UPLOAD_DOCUMENTS: [UserRole.EDUCATOR, UserRole.DIRECTIVE, UserRole.SUPERADMIN],

  // Payment permissions
  ACCESS_PAYMENTS_PANEL: [UserRole.SUPERADMIN],
  ACCESS_PAYMENTS_PORTAL: [UserRole.ATTENDANT, UserRole.STUDENT],

  // Content permissions
  ACCESS_CONTENT_EDITOR: [UserRole.DIRECTIVE, UserRole.SUPERADMIN],

  ACCESS_SETTINGS: [ UserRole.SUPERADMIN],

  // Educator permissions
  ACCESS_STUDENT_INFO: [UserRole.EDUCATOR, UserRole.ADMIN,UserRole.DIRECTIVE, UserRole.SUPERADMIN],
  SYNC_STUDENTS: [UserRole.SUPERADMIN]

} as const

export const hasPermission = (userRole: UserRole | null | undefined, permission: keyof typeof PERMISSIONS): boolean => {
  if (!userRole) return false
  return PERMISSIONS[permission].includes(userRole)
}


export const editorContentEmailException = ["secreacademica@jecaro.edu.co"];

/**
 * Emails con permiso elevado sobre la Biblioteca de Media (PJ-56):
 * - Pueden acceder a `/admin/media`.
 * - Pueden editar/eliminar TODOS los archivos (propios y ajenos), saltando la regla owner-only.
 * Cualquier otro usuario (incluido `dir` o `superadmin`) solo puede gestionar lo propio.
 */
export const mediaEmailException = [
  "secreacademica@jecaro.edu.co",
  "soporte@jecaro.edu.co",
];

/** Devuelve true si el email tiene permiso elevado para gestionar cualquier archivo de media. */
export const canManageAnyMedia = (email: string | null | undefined): boolean =>
  mediaEmailException.includes(email || "")

/**
 * Determina si un usuario puede editar/eliminar un MediaItem específico.
 * Fuente de verdad única — usada por el thunk (boundary de seguridad) y la UI (gating de botones).
 *
 * Reglas:
 *  - Owner (`item.createdBy === user.uid`), o
 *  - Email con permiso elevado (`mediaEmailException` → secreacademica/soporte).
 * Sin overrides por rol: `dir`/`superadmin` solo pueden gestionar lo propio.
 */
export const canManageMediaItem = (
  item: { createdBy: string },
  user: { uid?: string | null; email?: string | null } | null | undefined
): boolean => {
  if (!user?.uid) return false
  if (canManageAnyMedia(user.email)) return true
  return item.createdBy === user.uid
}

export const canAccessDocuments = (userRole: UserRole | null | undefined) => hasPermission(userRole, 'ACCESS_DOCUMENTS')
export const canUploadDocuments = (userRole: UserRole | null | undefined, email: string | undefined) => hasPermission(userRole, 'UPLOAD_DOCUMENTS') || editorContentEmailException.includes(email || "")
export const canAccessPaymentsPanel = (userRole: UserRole | null | undefined, email: string | undefined) => hasPermission(userRole, 'ACCESS_PAYMENTS_PANEL') || editorContentEmailException.includes(email || "")
export const canAccessPaymentsPortal = (userRole: UserRole | null | undefined) => hasPermission(userRole, 'ACCESS_PAYMENTS_PORTAL')
export const canAccessContent = (userRole: UserRole | null | undefined, email: string | undefined) => hasPermission(userRole, 'ACCESS_CONTENT_EDITOR') || editorContentEmailException.includes(email || "")
export const canAccessMedia = (userRole: UserRole | null | undefined, email: string | undefined) => hasPermission(userRole, 'ACCESS_CONTENT_EDITOR') || mediaEmailException.includes(email || "")
export const canAccessSettings = (userRole: UserRole | null | undefined, email: string | undefined) => hasPermission(userRole, 'ACCESS_SETTINGS') || editorContentEmailException.includes(email || "")
export const canSyncStudents = (userRole: UserRole | null | undefined, email: string | undefined) => hasPermission(userRole, 'SYNC_STUDENTS') || editorContentEmailException.includes(email || "")

export const canAccessStudentInfo = (userRole: UserRole | null | undefined) => hasPermission(userRole, 'ACCESS_STUDENT_INFO')


export const roleMap: Record<string, UserRole> = {
  'admin': UserRole.ADMIN,
  'dir': UserRole.DIRECTIVE,
  'educator': UserRole.EDUCATOR,
  'student': UserRole.STUDENT,
  'attendant': UserRole.ATTENDANT,
  'superadmin': UserRole.SUPERADMIN
}